/**
 * FlyoutPool — app-lifetime DOM element pool for flyout / card-icon visuals.
 *
 * The DOM-layer twin of the bill pool: build once, reuse forever, never destroy.
 * Flyouts (cash notes, bank icon, tie chip, trail ghosts) and card result pips
 * used to `createElement` + `.remove()` on every event — cheap individually, but
 * a steady stream of DOM create/destroy + GC + layout churn, which on a
 * constrained device is load we don't need to pay.
 *
 * Instead each visual `acquire`s a parked element of its `type` (a free list per
 * kind) — reset and ready — and `release`s it back on completion (in-flight gsap
 * tweens killed, animated styles cleared, detached from the DOM, parked). The
 * `factory` builds the typed element the first time only; every reuse after is
 * free. For variable-content visuals (cash notes of different denominations) the
 * caller just swaps the image `src` after acquire — the element is reused, only
 * the picture changes.
 *
 * Lives as a module singleton, so the pool survives leaving a room / starting a
 * new game, the same way the bill pool does.
 */

import gsap from 'gsap';

const POOL_TYPE = '__flyoutPoolType';

class FlyoutPool {
  private free = new Map<string, HTMLElement[]>();

  /**
   * Get a parked element of `type` (reset + ready) or build one via `factory`.
   * The factory should set the element's STABLE styling (className, position,
   * z-index) — that persists across reuses. The caller sets the per-use bits
   * (left/top/transform/src) after acquire.
   */
  acquire(type: string, factory: () => HTMLElement): HTMLElement {
    const list = this.free.get(type);
    const el = list && list.length ? list.pop()! : factory();
    (el as unknown as Record<string, string>)[POOL_TYPE] = type;
    this.resetAnimatedStyles(el);
    return el;
  }

  /** Park an element back (kills its tweens, clears animated styles, detaches). */
  release(el: HTMLElement): void {
    const type = (el as unknown as Record<string, string>)[POOL_TYPE];
    gsap.killTweensOf(el);
    el.parentElement?.removeChild(el);
    this.resetAnimatedStyles(el);
    if (!type) return; // not a pooled element — just dropped
    let list = this.free.get(type);
    if (!list) { list = []; this.free.set(type, list); }
    list.push(el);
  }

  /** Convenience for a batch (e.g. a whole cash-flyout's notes + trails). */
  releaseAll(els: HTMLElement[]): void {
    for (const el of els) this.release(el);
  }

  /**
   * Clear only the inline styles the flyout ANIMATIONS mutate, so a reused
   * element starts pristine. The factory's stable styles (position/zIndex/
   * className) and the per-use styles the caller re-sets on acquire are left
   * for the caller to own.
   */
  private resetAnimatedStyles(el: HTMLElement): void {
    // clearProps also resets GSAP's internal transform cache on the element, so
    // a reused element doesn't inherit a stale scale/position from its last
    // flight (the pooling hazard). left/top aren't transform props, clear them
    // directly. The caller re-sets left/top/transform on acquire.
    gsap.set(el, { clearProps: 'transform,opacity,scale,x,y,rotation' });
    el.style.left = '';
    el.style.top = '';
  }
}

export const flyoutPool = new FlyoutPool();
